import {create} from "apisauce";

import {QUICKSIGHT_BASE_PATH} from "../config";
import {getAuthorizationHeader} from "../models/admin-session/selectors";

/** OpenAPI `dashboard` */
export type QuicksightDashboard = Readonly<{
  id: string;
  name: string;
}>;

/** OpenAPI `dashboards` */
type DashboardsResponse = Readonly<{
  dashboards: ReadonlyArray<QuicksightDashboard>;
}>;

/** POST /admin/dashboards — one selected user's granted dashboards */
export type QuicksightUserDashboards = Readonly<{
  email: string;
  dashboards: ReadonlyArray<QuicksightDashboard>;
}>;

/** OpenAPI `admin-dashboard-payload` */
export type AdminDashboardPayload = Readonly<{
  emails: ReadonlyArray<string>;
}>;

/**
 * OpenAPI `admin-dashboards`
 *
 * `dashboards` is the grantable catalog for the caller (Reside admin: all
 * dashboards; org admin: dashboards they can grant). `users` is the currently
 * granted set for each requested email, including emails with none.
 */
export type UsersDashboardsResponse = Readonly<{
  dashboards: ReadonlyArray<QuicksightDashboard>;
  users: ReadonlyArray<QuicksightUserDashboards>;
}>;

/** OpenAPI `dashboard-url` */
type DashboardUrlResponse = Readonly<{
  uri: string;
}>;

/** OpenAPI `dashboard-user` */
export type QuicksightDashboardUser = Readonly<{
  dashboardId: string;
  email: string;
}>;

/** OpenAPI `dashboard-permission` */
export type QuicksightDashboardPermission = Readonly<{
  selected: boolean;
  dashboardUsers: ReadonlyArray<QuicksightDashboardUser>;
}>;

const http = create({
  baseURL: QUICKSIGHT_BASE_PATH,
  headers: {
    Accept: "application/json",
  },
});

http.addRequestTransform(request => {
  request.headers = request.headers || {};
  request.headers.Authorization = getAuthorizationHeader();
});

http.addResponseTransform(response => {
  if (response.problem) {
    throw response;
  }
});

const mapDashboardList = (
  dashboards?: ReadonlyArray<QuicksightDashboard>,
): ReadonlyArray<QuicksightDashboard> =>
  (dashboards ?? []).map(dashboard => ({
    id: dashboard.id,
    name: dashboard.name,
  }));

const mapDashboards = (data?: DashboardsResponse): DashboardsResponse => ({
  dashboards: mapDashboardList(data?.dashboards),
});

const mapUsersDashboards = (
  data?: UsersDashboardsResponse,
): UsersDashboardsResponse => ({
  dashboards: mapDashboardList(data?.dashboards),
  users: (data?.users ?? []).map(user => ({
    email: user.email,
    dashboards: mapDashboardList(user.dashboards),
  })),
});

export const QuicksightService = {
  /** GET /dashboards/{id}/uri */
  getDashboard: (dashboardId: string) =>
    http.get<DashboardUrlResponse>(
      `/dashboards/${encodeURIComponent(dashboardId)}/uri`,
    ),

  /** POST /dashboards/permissions for a single email */
  putPermissions: (
    email: string,
    data: {dashboardIds: ReadonlyArray<string>; selected: boolean},
  ) =>
    QuicksightService.putPermissionsBulk({
      selected: data.selected,
      dashboardUsers: data.dashboardIds.map(dashboardId => ({
        dashboardId,
        email,
      })),
    }),

  /** POST /dashboards/permissions */
  putPermissionsBulk: (data: QuicksightDashboardPermission) => {
    if (data.dashboardUsers.length === 0) {
      return Promise.resolve({ok: true, data: {}});
    }

    return http.post("/dashboards/permissions", {
      selected: data.selected,
      dashboardUsers: data.dashboardUsers,
    });
  },

  /** GET /admin/dashboards */
  getAdminDashboards: async () => {
    const response = await http.get<DashboardsResponse>("/admin/dashboards");

    return {...response, data: mapDashboards(response.data)};
  },

  /**
   * POST /admin/dashboards
   * Body: `{ emails }`. Same contract for one user or many.
   */
  getUsersDashboards: async (emails: ReadonlyArray<string>) => {
    if (emails.length === 0) {
      return Promise.resolve({
        ok: true,
        data: {dashboards: [], users: []},
      });
    }

    const payload: AdminDashboardPayload = {emails: [...emails]};
    const response = await http.post<UsersDashboardsResponse>(
      "/admin/dashboards",
      payload,
    );

    return {...response, data: mapUsersDashboards(response.data)};
  },

  /** POST /admin/dashboards with a single email */
  getUserDashboards: async (email: string) => {
    const response = await QuicksightService.getUsersDashboards([email]);
    const dashboards =
      response.data.users.find(
        user => user.email.toLowerCase() === email.toLowerCase(),
      )?.dashboards ?? [];

    return {...response, data: {dashboards}};
  },

  /** GET /dashboards */
  getCurrentUserDashboards: async () => {
    const response = await http.get<DashboardsResponse>("/dashboards");

    return {...response, data: mapDashboards(response.data)};
  },

  /** POST /dashboards/sync */
  syncDashboards: () => http.post("/dashboards/sync", {}),
};
